import { useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

/**
 * Params that can be read from the URL query string.
 * All are optional — each tool reads only the subset it needs.
 */
export interface ToolInitParams {
  /** Generic color (hex without #) — used by formats, nearest, encyclopedia, etc. */
  color?: string;
  /** Foreground color — used by compare, accessibility */
  fg?: string;
  /** Background color — used by compare, accessibility */
  bg?: string;
  /** Search query — used by semantic, nearest */
  q?: string;
  /** Palette ID — used by audit, gradient */
  palette?: string;
  /** Mode selector — used by gradient (freeform|palette) */
  mode?: string;
}

/**
 * Richer state passed via `navigate(path, { state })`.
 * React Router serializes this, so it survives forward/back navigation
 * but not page refresh (intentional).
 */
export interface ToolInitState {
  /** Array of hex colors — used by palette, gradient for pre-seeding */
  colors?: string[];
  /** Source tool — for breadcrumb/back navigation */
  from?: string;
  /** Any extra key-value pairs */
  [key: string]: unknown;
}

/** Normalize a hex-like param: add # prefix if missing */
function normalizeHex(value: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/**
 * Hook that reads URL search params + Router location state on mount.
 * 
 * - Returns parsed `ToolInitParams` from the URL query string.
 * - Returns `ToolInitState` from `location.state`.
 * - Cleans the URL (removes search params) after the first read
 *   so that refresh doesn't re-apply stale values.
 * 
 * Usage:
 * ```tsx
 * const { params, state } = useToolInit();
 * // params.color, params.fg, params.bg, etc.
 * // state.colors, state.from, etc.
 * ```
 */
export function useToolInit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const hasRead = useRef(false);

  // Parse URL params once on mount
  const params = useMemo<ToolInitParams>(() => {
    if (hasRead.current) return {};
    
    const result: ToolInitParams = {};
    
    const color = searchParams.get('color');
    if (color) result.color = normalizeHex(color);

    const fg = searchParams.get('fg');
    if (fg) result.fg = normalizeHex(fg);

    const bg = searchParams.get('bg');
    if (bg) result.bg = normalizeHex(bg);

    const q = searchParams.get('q');
    if (q) result.q = q.trim();

    const palette = searchParams.get('palette');
    if (palette) result.palette = palette.trim();

    const mode = searchParams.get('mode');
    if (mode) result.mode = mode.trim();

    return result;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only on mount

  // Parse Router location state once
  const state = useMemo<ToolInitState>(() => {
    if (hasRead.current) return {};
    return (location.state as ToolInitState) ?? {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only on mount

  // Clean URL params after first read
  useEffect(() => {
    if (hasRead.current) return;
    hasRead.current = true;

    // Only clean if there were params to begin with
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-time cleanup

  return { params, state };
}
